'use client'
import React, {
	createContext,
	useContext,
	useMemo,
	useState,
	useCallback,
} from 'react'

import { SessionData } from '@/app/adapters/types'
import { ListingsApiData } from '@/app/service/models/biListings'
import { FieldEnum } from '@/lib/components/business-intelligence/bi-listings-transactions/DataTable/enums'
import { Strings } from '@/lib/constants/strings'
import { SegmentType } from '@/lib/enums/bi-enums'
import { TableState, TableActions } from '../../../app/utilities/tableUtils'
import { useTableData } from '../../hooks/useTableData'
import { usePreferences } from '../preferencesContext'

interface ListingsContextProps {
	state: TableState<ListingsApiData>
	actions: TableActions & {
		setActiveSegmentIndex: (index: number) => void
	}
	fetchData: () => Promise<void>
	activeSegmentIndex: number
}

const ListingsContext = createContext<ListingsContextProps | undefined>(
	undefined
)

export const ListingsProvider: React.FC<{
	children: React.ReactNode
	sessionData: SessionData
}> = ({ children, sessionData }) => {
	const { busIntelPreferences } = usePreferences()

	const metricPeriodTypeCdValue: string =
		busIntelPreferences?.preferences?.dealPipelineListings
			?.metricPeriodTypeCd ?? Strings.REVENUE_SUMMARY_PERIOD_DEFAULT

	const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(
		SegmentType[metricPeriodTypeCdValue as keyof typeof SegmentType] ?? 0
	)
	const { state, actions, fetchData } = useTableData({
		dataSource: 'listings',
		errorMessage: 'Error fetching listings data:',
		sessionData,
		activeSegmentIndex,
		defaultSortField: FieldEnum.ListingDate,
		defaultSortDirection: 'desc',
	})
	const setActiveSegmentIndexCallback = useCallback((index: number) => {
		setActiveSegmentIndex(index)
	}, [])

	const enhancedActions = useMemo(
		() => ({
			...actions,
			setActiveSegmentIndex: setActiveSegmentIndexCallback,
		}),
		[actions, setActiveSegmentIndexCallback]
	)

	const value = useMemo(
		() => ({
			state,
			actions: enhancedActions,
			fetchData,
			activeSegmentIndex,
		}),
		[state, enhancedActions, fetchData, activeSegmentIndex]
	)

	return (
		<ListingsContext.Provider value={value}>
			{children}
		</ListingsContext.Provider>
	)
}

export const useListings = (): ListingsContextProps => {
	const context = useContext(ListingsContext)
	if (!context) {
		throw new Error('useListings must be used within a ListingsProvider')
	}
	return context
}
